/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { makeAutoObservable } from 'mobx';

export type Theme = 'light' | 'dark';

const THEME_KEY = 'therapy.theme';

function readInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/**
 * Cross-cutting UI state shared by the portal shell: the colour theme (the
 * top-bar dark-mode toggle, Figma) and the global search query that the top-bar
 * search box drives and list pages read to filter.
 */
export class UiStore {
  public theme: Theme = 'light';
  public search = '';

  public constructor() {
    makeAutoObservable(this);
    this.theme = readInitialTheme();
    this.applyTheme();
  }

  public toggleTheme(): void {
    this.theme = this.theme === 'light' ? 'dark' : 'light';
    try {
      localStorage.setItem(THEME_KEY, this.theme);
    } catch {
      // Ignore storage failures (private mode); the in-memory theme still applies.
    }
    this.applyTheme();
  }

  public setSearch(value: string): void {
    this.search = value;
  }

  public clearSearch(): void {
    this.search = '';
  }

  private applyTheme(): void {
    document.documentElement.classList.toggle('dark', this.theme === 'dark');
  }
}

export const uiStore = new UiStore();
